import { KnownAccessControlDtoStatus, KnownSettingsNodeDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useCallback, useMemo, useState } from 'react';
import { useQueryGetProject } from '../../../../core/queries/projects.queries';
import { useQueryGetHub } from '../../../lockers/hubs.queries';
import {
  useMutationAddAccessCodes,
  useMutationRemoveAccessCodes,
  useMutationUpdateAccessControl,
  useQueryGetAccessControl
} from '../../../access-control/queries';
import { EntityForm } from '../form.types';
import { AccessControlForm } from './form';
import { AccessControlFormOutput } from './form-output';
import { AccessControlFormSchema } from './form-schema';
import { accessControlFormUISchema } from './ui-schema';
import { AccessControlCodesField } from './access-control-codes-field';

export function useEditAccessControlForm(
  accessControlId: string | undefined,
  parentEntityId: string | undefined,
  parentNodeType: KnownSettingsNodeDtoType | undefined
): EntityForm | null {
  const hubResult = useQueryGetHub(parentEntityId);
  const projectResult = useQueryGetProject(parentEntityId);
  const parent = parentNodeType === KnownSettingsNodeDtoType.Hub ? hubResult : projectResult;

  const accessControlResult = useQueryGetAccessControl(accessControlId);
  const updateMutation = useMutationUpdateAccessControl();
  const addCodesMutation = useMutationAddAccessCodes();
  const removeCodesMutation = useMutationRemoveAccessCodes();

  const parentDisplayName = parent.data?.name;

  const serverCodes = useMemo(() => accessControlResult.data?.accessCodes?.codes ?? [], [accessControlResult.data]);

  // Tracks the post-save snapshot so codes lock immediately after save
  // without waiting for the refetch to re-render.
  const [savedCodesSnapshot, setSavedCodesSnapshot] = useState<string[]>([]);

  const initialCodes = useMemo(
    () => [...new Set([...serverCodes, ...savedCodesSnapshot])],
    [serverCodes, savedCodesSnapshot]
  );

  const isDeleted = accessControlResult.data?.status === KnownAccessControlDtoStatus.Deleted;

  const formContext = useMemo(() => ({ initialCodes, isDeleted }), [initialCodes, isDeleted]);

  const defaultValues = useMemo((): AccessControlForm => {
    if (!accessControlResult.data || !parentEntityId) return {};
    return AccessControlFormOutput.FromDto(accessControlResult.data, parentEntityId);
  }, [accessControlResult.data, parentEntityId]);

  const initialFormOutput = useMemo(() => {
    return new AccessControlFormOutput(defaultValues);
  }, [defaultValues]);

  const formSchema = useMemo(() => {
    return AccessControlFormSchema.FromFormData(parentEntityId, parentDisplayName, true, isDeleted);
  }, [parentEntityId, parentDisplayName, isDeleted]);

  const onSubmit = useCallback(
    async (formOutput: AccessControlFormOutput) => {
      if (!accessControlId) return;

      const result = await updateMutation.mutateAsync({
        accessControlID: accessControlId,
        dto: AccessControlFormOutput.ToEditDto(formOutput)
      });

      const newCodes = (formOutput.codes ?? []).filter(Boolean);
      const codesToAdd = newCodes.filter((c) => !serverCodes.includes(c));
      const codesToRemove = serverCodes.filter((c) => !newCodes.includes(c));

      if (codesToAdd.length > 0) {
        await addCodesMutation.mutateAsync({ accessControlID: accessControlId, codes: codesToAdd });
      }
      if (codesToRemove.length > 0) {
        await removeCodesMutation.mutateAsync({ accessControlID: accessControlId, codes: codesToRemove });
      }

      setSavedCodesSnapshot(newCodes);

      await accessControlResult.refetch();
      return { entityId: result.id };
    },
    [accessControlId, updateMutation, addCodesMutation, removeCodesMutation, serverCodes, accessControlResult]
  );

  if (!accessControlId) return null;

  return {
    isLoading: accessControlResult.isLoading || parent.isLoading,
    title: 'Access Control Settings',
    subtitle: accessControlId,
    allowSubtitleCopy: true,
    defaultValues,
    initialFormOutput,
    formSchema,
    uiSchema: accessControlFormUISchema,
    fields: { codesEditor: AccessControlCodesField },
    formContext,
    getOutput(formData) {
      return new AccessControlFormOutput(formData as AccessControlForm);
    },
    onSubmit,
    isLocked: false,
    lockedMessage: null
  };
}
